import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import './MapComponent.css';
import 'leaflet.markercluster';
import SearchControl from './SearchControl';
import VisibleItemsList from './VisibleItemsList';
import apiService from '../services/apiService';
import { buildPopupContent } from '../utils/popupBuilder';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const BoundsTracker = ({ onBoundsChange }) => {
  const map = useMapEvents({
    moveend: () => {
      onBoundsChange(map.getBounds());
    },
    zoomend: () => {
      onBoundsChange(map.getBounds());
    },
    load: () => {
      onBoundsChange(map.getBounds());
    }
  });

  useEffect(() => {
    onBoundsChange(map.getBounds());
  }, [map, onBoundsChange]);

  return null;
};

const GeoJSONLayers = ({ geoJsonData, activeLayers, getStyle, onEachFeature, pointToLayer, markerLayersRef }) => {
  const map = useMap();
  const [clusterGroup, setClusterGroup] = useState(null);

  useEffect(() => {
    const cluster = L.markerClusterGroup({
      chunkedLoading: true,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      maxClusterRadius: 80
    });
    
    map.addLayer(cluster);
    setClusterGroup(cluster);

    return () => {
      map.removeLayer(cluster);
    };
  }, [map]);

  useEffect(() => {
    if (!clusterGroup) return;

    clusterGroup.clearLayers();
    const newMarkerLayers = {};

    Object.entries(geoJsonData).forEach(([layerName, layerInfo]) => {
      if (activeLayers[layerName]) {
        const geoJsonLayer = L.geoJSON(layerInfo.data, {
          style: getStyle(layerInfo.color),
          onEachFeature: onEachFeature,
          pointToLayer: (feature, latlng) => pointToLayer(feature, latlng, layerInfo.color)
        });

        geoJsonLayer.eachLayer((layer) => {
          if (layer instanceof L.CircleMarker || layer instanceof L.Marker) {
            clusterGroup.addLayer(layer);
            
            // Speichere Referenz zum Layer mit Koordinaten als Key
            const coords = layer.getLatLng();
            const key = `${coords.lat.toFixed(6)}_${coords.lng.toFixed(6)}`;
            newMarkerLayers[key] = { layer, clusterGroup };
          } else {
            layer.addTo(map);
          }
        });
      }
    });

    // Aktualisiere die Referenz
    if (markerLayersRef) {
      markerLayersRef.current = newMarkerLayers;
    }

    return () => {
      map.eachLayer((layer) => {
        if (layer instanceof L.GeoJSON || (layer instanceof L.Path && !(layer instanceof L.CircleMarker))) {
          map.removeLayer(layer);
        }
      });
    };
  }, [clusterGroup, geoJsonData, activeLayers, map, getStyle, onEachFeature, pointToLayer, markerLayersRef]);

  return null;
};

const MapController = ({ mapRef }) => {
  const map = useMap();
  
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);
  
  return null;
};

const MapComponent = ({ activeLayers }) => {
  const [geoJsonData, setGeoJsonData] = useState({});
  const [layerConfig, setLayerConfig] = useState({}); // { name: { color, route } }
  const [visibleItems, setVisibleItems] = useState([]);
  const [mapBounds, setMapBounds] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const markerLayersRef = useRef({});
  const mapRef = useRef(null);
  const loadingLayersRef = useRef({});
  const prevActiveLayersRef = useRef({});

  // Lade Layer-Konfiguration (Farben und Routes) beim Start
  useEffect(() => {
    const loadLayerConfig = async () => {
      try {
        const layersResponse = await apiService.getGeoJsonLayers();
        const config = {};
        layersResponse.layers.forEach(layer => {
          config[layer.name] = { 
            color: layer.color, 
            route: layer.route || layer.name // Fallback auf name wenn route nicht vorhanden
          };
        });
        setLayerConfig(config);
      } catch (error) {
        console.error('Error loading layer config:', error);
      }
    };
    loadLayerConfig();
  }, []);

  // Lade Layer-Daten bei jedem Aktivieren neu vom Server (nutzt route statt name)
  useEffect(() => {
    const loadActivatedLayers = async () => {
      for (const [layerName, isActive] of Object.entries(activeLayers)) {
        const wasActive = prevActiveLayersRef.current[layerName];
        
        // Lade nur wenn Layer gerade aktiviert wurde (nicht vorher aktiv) und nicht bereits lädt
        if (isActive && !wasActive && !loadingLayersRef.current[layerName]) {
          loadingLayersRef.current[layerName] = true;
          
          try {
            // Nutze route für API-Aufruf, fallback auf layerName
            const layerRoute = layerConfig[layerName]?.route || layerName;
            const data = await apiService.getGeoJsonLayer(layerRoute);
            const color = layerConfig[layerName]?.color || '#3388ff';
            
            setGeoJsonData(prev => ({
              ...prev,
              [layerName]: { data, color }
            }));
          } catch (error) {
            console.error(`Error loading layer ${layerName}:`, error);
          } finally {
            loadingLayersRef.current[layerName] = false;
          }
        }
      }
    };

    loadActivatedLayers();
    
    // Speichere aktuellen Zustand für nächsten Vergleich
    prevActiveLayersRef.current = { ...activeLayers };
  }, [activeLayers, layerConfig]);

  const loadGeoJsonLayer = useCallback(async (layerName) => {
    if (geoJsonData[layerName]) {
      return geoJsonData[layerName];
    }

    if (loadingLayersRef.current[layerName]) {
      return null;
    }

    loadingLayersRef.current[layerName] = true;
    try {
      const layerRoute = layerConfig[layerName]?.route || layerName;
      const data = await apiService.getGeoJsonLayer(layerRoute);
      const color = layerConfig[layerName]?.color || '#3388ff';
      const layerInfo = { data, color };
      setGeoJsonData(prev => ({
        ...prev,
        [layerName]: layerInfo
      }));
      return layerInfo;
    } catch (error) {
      console.error(`Error loading layer ${layerName}:`, error);
      return null;
    } finally {
      loadingLayersRef.current[layerName] = false;
    }
  }, [geoJsonData, layerConfig]);

  const filterVisibleItems = useCallback((bounds) => {
    if (!bounds || !geoJsonData) return;

    const items = [];
    
    // Iteriere über alle geladenen Layer dynamisch
    Object.entries(geoJsonData).forEach(([layerName, layerInfo]) => {
      // Nur Layer berücksichtigen die aktiv sind
      if (activeLayers[layerName] && layerInfo?.data?.features) {
        layerInfo.data.features.forEach((feature) => {
          if (feature.geometry.type === 'Point') {
            const [lng, lat] = feature.geometry.coordinates;
            if (bounds.contains([lat, lng])) {
              // Bestimme Typ: Prüfe Layer-Name UND Feature-Properties
              const layerNameLower = layerName.toLowerCase();
              const healthcare = feature.properties.healthcare_type?.toLowerCase() || '';
              
              // MVZ Erkennung: Layer enthält "mvz" ODER healthcare property ist "mvz"
              const isMvz = layerNameLower.includes('mvz') || healthcare === 'mvz';
              const itemType = isMvz ? 'mvz' : 'doctor';
              
              items.push({
                type: itemType,
                name: feature.properties.name || 'Unbekannt',
                address: feature.properties['addr:street'] 
                  ? `${feature.properties['addr:street']} ${feature.properties['addr:housenumber'] || ''}, ${feature.properties['addr:postcode'] || ''} ${feature.properties['addr:city'] || ''}`
                  : feature.properties.address || '',
                specialty: feature.properties.healthcare || feature.properties['healthcare:speciality'] || '',
                lat,
                lng
              });
            }
          }
        });
      }
    });

    items.sort((a, b) => a.name.localeCompare(b.name));
    setVisibleItems(items);
  }, [geoJsonData, activeLayers]);

  const handleBoundsChange = useCallback((bounds) => {
    setMapBounds(bounds);
    filterVisibleItems(bounds);
  }, [filterVisibleItems]);

  const handleItemClick = useCallback((item) => {
    if (!mapRef.current) return;
    
    // Prüfe ob das gleiche Item nochmal geklickt wurde (Toggle)
    const isSameItem = selectedItem && 
      selectedItem.lat === item.lat && 
      selectedItem.lng === item.lng;
    
    if (isSameItem) {
      // Schließe Popup
      mapRef.current.closePopup();
      setSelectedItem(null);
    } else {
      // Zoom auf hohe Stufe damit der Marker nicht mehr geclustert ist
      const targetZoom = 18;
      
      mapRef.current.flyTo([item.lat, item.lng], targetZoom, {
        duration: 0.5
      });
      
      // Nach dem Zoom den Marker finden und Popup öffnen
      const openPopupAfterZoom = () => {
        // Suche den Marker durch alle Layer auf der Karte
        let foundLayer = null;
        
        mapRef.current.eachLayer((layer) => {
          if (foundLayer) return;
          
          // Prüfe ob es ein CircleMarker oder Marker ist
          if ((layer instanceof L.CircleMarker || layer instanceof L.Marker) && layer.getLatLng) {
            const layerLatLng = layer.getLatLng();
            const distance = Math.abs(layerLatLng.lat - item.lat) + Math.abs(layerLatLng.lng - item.lng);
            
            // Toleranz für Koordinatenvergleich
            if (distance < 0.0001) {
              foundLayer = layer;
            }
          }
        });
        
        if (foundLayer && foundLayer.openPopup) {
          foundLayer.openPopup();
        }
      };
      
      // Warte bis der Zoom abgeschlossen ist
      mapRef.current.once('moveend', () => {
        // Kurze Verzögerung damit der Cluster aufgelöst wird
        setTimeout(openPopupAfterZoom, 100);
      });
      
      setSelectedItem(item);
    }
  }, [selectedItem]);

  useEffect(() => {
    if (mapBounds) {
      filterVisibleItems(mapBounds);
    }
  }, [activeLayers, mapBounds, filterVisibleItems]);

  const getStyle = (color) => {
    return {
      color: color,
      weight: 2,
      opacity: 0.7,
      fillOpacity: 0.5
    };
  };

  const onEachFeature = (feature, layer) => {
    if (feature.properties) {
      const props = feature.properties;
      const popupContent = buildPopupContent(props);
      layer.bindPopup(popupContent, {
        maxWidth: 360,
        minWidth: 280,
        className: 'custom-popup'
      });
      
      if ((feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon')) {
        const labelParts = [];
        
        if (feature.properties.missingDoctors !== undefined) {
          labelParts.push(`<span style="color: #ff0000; font-weight: bold;">${feature.properties.missingDoctors}</span>`);
        }
        
        if (feature.properties.population !== undefined) {
          labelParts.push(`<span style="color: #333; font-weight: bold;">${feature.properties.population}</span>`);
        }
        
        if (labelParts.length > 0) {
          layer.bindTooltip(labelParts.join(' / '), {
            permanent: true,
            direction: 'center',
            className: 'polygon-label-tooltip'
          });
        }
      }
    }
  };

  const pointToLayer = (feature, latlng, color) => {
    // Feature kann eine eigene Farbe mitbringen (z. B. Risiko-Einfärbung je Praxis);
    // sonst die Layer-Farbe verwenden.
    const fillColor = feature.properties?.['marker-color'] || color;
    return L.circleMarker(latlng, {
      radius: 6,
      fillColor,
      color: '#fff',
      weight: 1,
      opacity: 1,
      fillOpacity: 0.8
    });
  };

  return (
    <div className="map-wrapper">
      <MapContainer 
        center={[51.1657, 10.4515]} 
        zoom={6} 
        className="map-container"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <SearchControl
          geoJsonData={geoJsonData}
          activeLayers={activeLayers}
          layerConfig={layerConfig}
          loadGeoJsonLayer={loadGeoJsonLayer}
        />
        <BoundsTracker onBoundsChange={handleBoundsChange} />
        <MapController mapRef={mapRef} />
        
        <GeoJSONLayers
          geoJsonData={geoJsonData}
          activeLayers={activeLayers}
          getStyle={getStyle}
          onEachFeature={onEachFeature}
          pointToLayer={pointToLayer}
          markerLayersRef={markerLayersRef}
        />
      </MapContainer>
      
      <VisibleItemsList 
        visibleItems={visibleItems} 
        onItemClick={handleItemClick}
        selectedItem={selectedItem}
      />
    </div>
  );
};

export default MapComponent;
